import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Music, Sparkles, UserPlus, LogIn } from 'lucide-react';

const BACKGROUND_IMAGE = '/assets/migrated/background.png';
const LOGO_IMAGE = '/assets/brand/logo-inline.png';

export default function DemoSongDetail() {
  const { demoId } = useParams();
  const navigate = useNavigate();
  const [demo, setDemo] = useState(null);
  const [error, setError] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const pollingRef = useRef(null);

  // Ticks once per second while the demo is still processing so the
  // elapsed counter under the status line stays fresh. Cleared once
  // the song completes / fails.
  useEffect(() => {
    const status = demo?.song?.status;
    if (status === 'completed' || status === 'failed') return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [demo?.song?.status]);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await apiFetch(`/demo/songs/${demoId}`);
        if (cancelled) return;
        setDemo(data);
        const status = data?.song?.status;
        if (status === 'completed' || status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Demo not found or expired');
        if (pollingRef.current) clearInterval(pollingRef.current);
      }
    };
    fetchOnce();
    pollingRef.current = setInterval(fetchOnce, 4000);
    return () => {
      cancelled = true;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [demoId]);

  const song = demo?.song;
  const status = song?.status;
  const audioUrl = song?.audio_url || song?.audio_url_2;

  const goSignup = () => navigate('/auth?mode=signup');
  const goLogin = () => navigate('/auth?mode=login');

  if (error) {
    return (
      <FramedShell>
        <div className="text-center py-16" data-testid="demo-error">
          <h1 className="text-2xl font-light mb-3">This demo can&apos;t be found</h1>
          <p className="text-white/70 mb-6">Demos expire after 48 hours. Sign up to create your own — it&apos;s free.</p>
          <Button onClick={goSignup} className="px-6 py-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 text-white">
            Sign up free
          </Button>
        </div>
      </FramedShell>
    );
  }

  if (!demo) {
    return (
      <FramedShell>
        <div className="text-center py-16">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-white/60" />
        </div>
      </FramedShell>
    );
  }

  if (status !== 'completed' && status !== 'failed') {
    // Prefer the server-side created_at (survives page refresh) and
    // fall back to page-load time so the counter still ticks if the
    // API forgot to serialise the timestamp.
    const startedIso = song?.created_at || demo?.created_at;
    const startedMs = startedIso ? new Date(startedIso).getTime() : nowMs;
    const elapsed = Math.max(0, Math.floor((nowMs - startedMs) / 1000));
    const mm = Math.floor(elapsed / 60);
    const ss = String(elapsed % 60).padStart(2, '0');
    return (
      <FramedShell>
        <div className="text-center py-16" data-testid="demo-processing">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 rounded-full bg-purple-500/30 animate-ping" />
            <Music className="relative h-16 w-16 text-purple-300" />
          </div>
          <h1 className="text-2xl md:text-3xl font-light mb-3">Composing your demo…</h1>
          <p className="text-white/70">
            {song?.title ? <>“{song.title}” — </> : null}
            usually ready in about two minutes.
          </p>
          <div
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 font-mono text-2xl tracking-wider text-white"
            data-testid="demo-elapsed-counter"
            aria-label={`Elapsed ${mm} minute${mm === 1 ? '' : 's'} ${ss} seconds`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
            {mm}:{ss}
          </div>
          <p className="text-white/40 text-xs mt-3">Status: {status || 'starting'}</p>
        </div>
      </FramedShell>
    );
  }

  if (status === 'failed') {
    return (
      <FramedShell>
        <div className="text-center py-16" data-testid="demo-failed">
          <h1 className="text-2xl font-light mb-3">Something went wrong</h1>
          <p className="text-white/70 mb-6">Our demo engine hiccupped. Sign up and try again — you&apos;ll get free credits.</p>
          <Button onClick={goSignup} className="px-6 py-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 text-white">
            Sign up free
          </Button>
        </div>
      </FramedShell>
    );
  }

  return (
    <FramedShell>
      <div className="text-center mb-8" data-testid="demo-detail">
        <h1 className="text-2xl md:text-4xl font-light mb-2">{song.title || 'Your Demo Song'}</h1>
        <p className="text-white/60 text-sm capitalize">{song.genre} · {song.mood} · {song.voice} vocals</p>
      </div>

      {audioUrl ? (
        <div className="relative bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-md">
          {/* Diagonal watermark stripe visible in any screen recording */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-2xl select-none"
            aria-hidden="true"
            data-testid="demo-watermark-overlay"
          >
            <div
              className="text-white/[0.06] font-black tracking-[0.35em] whitespace-nowrap"
              style={{
                transform: 'rotate(-18deg)',
                fontSize: 'clamp(2.5rem, 8vw, 5rem)',
                letterSpacing: '0.3em',
              }}
            >
              DEMO · SIGN UP TO KEEP · DEMO · SIGN UP TO KEEP
            </div>
          </div>
          <div className="relative flex items-center gap-2 text-xs text-amber-300 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 font-medium tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> DEMO PREVIEW
            </span>
            <span className="text-white/50">
              Ending is faded out — sign up to keep the full track
            </span>
          </div>
          <audio
            controls
            src={audioUrl}
            className="relative w-full"
            data-testid="demo-audio-player"
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
          >
            Your browser does not support audio playback.
          </audio>
        </div>
      ) : (
        <div className="text-center text-white/50 mb-6">Audio not available.</div>
      )}

      {song.lyrics && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-md">
          <h3 className="text-lg font-medium mb-3">Lyrics</h3>
          <pre className="text-zinc-300 whitespace-pre-wrap text-sm font-mono">{song.lyrics}</pre>
        </div>
      )}

      {/* CLAIM CTA — the star of the page */}
      <div className="rounded-2xl p-8 mb-8 bg-gradient-to-br from-purple-600/30 to-purple-900/30 border-2 border-purple-500/50 shadow-xl shadow-purple-500/20 text-center" data-testid="demo-claim-cta">
        <Sparkles className="h-10 w-10 text-purple-300 mx-auto mb-3" />
        <h2 className="text-xl md:text-2xl font-light mb-2">Like your song?</h2>
        <p className="text-white/80 mb-6">
          Sign up free — we&apos;ll drop this song into your account (unlocked) and give you free
          credits to make more.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button
            onClick={goSignup}
            className="px-8 py-6 rounded-full bg-white text-slate-900 hover:bg-white/90 font-medium text-lg shadow-lg"
            data-testid="demo-signup-btn"
          >
            <UserPlus className="mr-2 h-5 w-5" /> Sign up free
          </Button>
          <Button
            variant="outline"
            onClick={goLogin}
            className="px-8 py-6 rounded-full border-2 border-white/40 bg-transparent hover:bg-white/10 text-white font-medium text-lg"
            data-testid="demo-login-btn"
          >
            <LogIn className="mr-2 h-5 w-5" /> I have an account
          </Button>
        </div>
      </div>

      <p className="text-center text-white/40 text-xs">
        This demo will expire in 48 hours if not claimed. Locked demo songs can&apos;t be shared or
        downloaded — sign up to unlock full access.
      </p>
    </FramedShell>
  );
}

/** Shared page frame — background + logo + back button. */
function FramedShell({ children }) {
  const navigate = useNavigate();
  return (
    <div
      className="min-h-screen text-white relative"
      style={{
        backgroundImage: `url('${BACKGROUND_IMAGE}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="absolute inset-0 bg-slate-950/60" />
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/')}
          className="text-white/60 hover:text-white text-sm mb-6 inline-flex items-center gap-2"
          data-testid="demo-detail-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </button>
        <div className="text-center mb-4">
          <img src={LOGO_IMAGE} alt="Wrapped In Music" className="h-8 md:h-10 mx-auto" />
        </div>
        {children}
      </div>
    </div>
  );
}
